import { z } from 'zod';
import { idSchema, localDateSchema } from './common.js';
/**
 * Notices and homework — the two things parents actually open the app for.
 *
 * `title` and `body` here are school-authored: a teacher's words, stored and
 * shown verbatim. They are data, not copy, and they never go near the message
 * catalogue (rule 9). Everything *around* them — "Due", "Whole school",
 * "Pinned" — is a key.
 */
export const audienceScopeSchema = z.enum(['SCHOOL', 'GRADE_LEVEL', 'SECTION']);
export const noticeSummarySchema = z.object({
    id: idSchema,
    title: z.string(),
    body: z.string(),
    scope: audienceScopeSchema,
    /** The grade or section named by the scope, ready to show. Null for a whole-school notice. */
    audienceName: z.string().nullable(),
    isPinned: z.boolean(),
    /** An instant — a publication time, read in the school's timezone. */
    publishedAt: z.string().nullable(),
    authorName: z.string(),
});
export const homeworkSummarySchema = z.object({
    id: idSchema,
    title: z.string().nullable(),
    body: z.string(),
    /** A local calendar date. Homework is due on a school day, not at an instant. */
    dueDate: localDateSchema,
    subjectName: z.string(),
    sectionName: z.string(),
    gradeLevelName: z.string(),
    postedByName: z.string(),
    publishedAt: z.string().nullable(),
    /** For a guardian reading several children's homework at once. */
    studentNames: z.array(z.string()).optional(),
});
export const homeworkQuerySchema = z.object({
    sectionId: idSchema.optional(),
    /** Inclusive window on the due date. Defaults to the current week at the school. */
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
});
export const noticeQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(30),
});
/**
 * Posting homework.
 *
 * A post is addressed to one section and one subject, because that is the pair
 * a teacher actually holds — `TeachingAssignment` is exactly this shape, and it
 * is what the server checks the request against. "Set it for the whole grade"
 * would read as one action and be four unrelated posts underneath, each of
 * which a different teacher may later need to correct.
 *
 * `dueDate` is a local calendar date. Homework is due on a school day, and a
 * parent reading this from another timezone must see the same Friday.
 */
export const createHomeworkSchema = z.object({
    sectionId: idSchema,
    subjectId: idSchema,
    /** Optional: plenty of homework is a sentence, not a titled assignment. */
    title: z.string().trim().max(160).optional(),
    body: z.string().trim().min(1).max(4000),
    dueDate: localDateSchema,
    notifyGuardians: z.boolean().default(true),
});
/**
 * Posting a notice.
 *
 * The scope decides who reads it, so the id it needs is required rather than
 * optional-and-hoped-for: a `GRADE_LEVEL` notice with no grade would be a
 * whole-school announcement by accident, which is the one mistake a school
 * cannot take back.
 */
export const createNoticeSchema = z
    .object({
    title: z.string().trim().min(1).max(160),
    body: z.string().trim().min(1).max(4000),
    scope: audienceScopeSchema,
    gradeLevelId: idSchema.optional(),
    sectionId: idSchema.optional(),
    isPinned: z.boolean().default(false),
})
    .superRefine((value, ctx) => {
    if (value.scope === 'GRADE_LEVEL' && !value.gradeLevelId) {
        ctx.addIssue({ code: 'custom', path: ['gradeLevelId'], message: 'required' });
    }
    if (value.scope === 'SECTION' && !value.sectionId) {
        ctx.addIssue({ code: 'custom', path: ['sectionId'], message: 'required' });
    }
});
/**
 * What a writer may post against.
 *
 * The compose form cannot guess this: a teacher's options are their teaching
 * assignments, and the office's are every section. Asking the server keeps the
 * form and the permission check reading from the same fact, so a picker can
 * never offer a class the save would refuse.
 */
export const homeworkTargetSchema = z.object({
    sectionId: idSchema,
    sectionName: z.string(),
    gradeLevelName: z.string(),
    subjects: z.array(z.object({ id: idSchema, name: z.string() })),
});
/** Where a notice may be addressed, for the same reason. */
export const noticeTargetsSchema = z.object({
    /** Whole-school notices are the office's to send, not a class teacher's. */
    canPostSchoolWide: z.boolean(),
    gradeLevels: z.array(z.object({ id: idSchema, name: z.string() })),
    sections: z.array(z.object({ id: idSchema, name: z.string() })),
});
//# sourceMappingURL=communication.js.map